import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** Monotonic token used to ignore stale gallery updates. */
  #variantUpdateRequestId = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const requestId = ++this.#variantUpdateRequestId;
    const isDesktopQuickAdd =
      window.matchMedia('(min-width: 750px)').matches &&
      Boolean(this.closest('#quick-add-modal-content[data-selu-desktop-quickbuy="true"]'));

    if (isDesktopQuickAdd) {
      const galleryReady = await this.#prepareDesktopQuickAddGallery(newMediaGallery);
      if (requestId !== this.#variantUpdateRequestId || !galleryReady) return;
    } else {
      const primaryImage = newMediaGallery.querySelector('img.product-media__image');

      if (primaryImage instanceof HTMLImageElement) {
        const imageReady = await this.#preloadImage(primaryImage);
        if (requestId !== this.#variantUpdateRequestId || !imageReady) return;
      }
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Prepares the actual image elements that will become visible in desktop
   * Quick Buy. The old gallery stays in the DOM until the new primary image is
   * loaded and decoded. Replacing the detached image node with the prepared
   * node avoids inserting an undecoded <img> that could briefly expose alt text.
   *
   * @param {Element} newMediaGallery
   * @returns {Promise<boolean>}
   */
  async #prepareDesktopQuickAddGallery(newMediaGallery) {
    const slides = Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
    const imageSlides = slides.filter((slide) => slide.querySelector('img.product-media__image'));

    if (imageSlides.length === 0) return true;

    slides.forEach((slide) => {
      slide.removeAttribute('data-selu-active');
      slide.setAttribute('aria-hidden', 'true');
    });

    const primarySlide = imageSlides[0];
    primarySlide.setAttribute('data-selu-active', '');
    primarySlide.setAttribute('aria-hidden', 'false');

    const primaryImage = primarySlide.querySelector('img.product-media__image');
    if (!(primaryImage instanceof HTMLImageElement)) return false;

    return this.#prepareDetachedImage(primaryImage, true);
  }

  /**
   * Creates and decodes the exact image element that will be inserted into the
   * detached gallery. All semantic attributes, including alt text, are kept.
   *
   * @param {HTMLImageElement} sourceImage
   * @param {boolean} isPrimary
   * @returns {Promise<boolean>}
   */
  #prepareDetachedImage(sourceImage, isPrimary) {
    const src = sourceImage.getAttribute('src');
    const srcset = sourceImage.getAttribute('srcset');
    const sizes = sourceImage.getAttribute('sizes');

    if (!src && !srcset) return Promise.resolve(false);

    return new Promise((resolve) => {
      const preparedImage = new Image();
      let settled = false;

      const finish = async (loaded) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);

        if (!loaded || preparedImage.naturalWidth <= 0) {
          resolve(false);
          return;
        }

        try {
          await preparedImage.decode();
        } catch {
          if (preparedImage.naturalWidth <= 0) {
            resolve(false);
            return;
          }
        }

        sourceImage.replaceWith(preparedImage);
        resolve(true);
      };

      const timeoutId = window.setTimeout(() => finish(false), 10000);
      preparedImage.onload = () => finish(true);
      preparedImage.onerror = () => finish(false);

      for (const attribute of sourceImage.attributes) {
        if (['src', 'srcset', 'sizes', 'loading', 'decoding', 'fetchpriority'].includes(attribute.name)) continue;
        preparedImage.setAttribute(attribute.name, attribute.value);
      }

      preparedImage.loading = 'eager';
      preparedImage.decoding = 'async';
      preparedImage.fetchPriority = isPrimary ? 'high' : 'auto';

      if (sizes) preparedImage.sizes = sizes;
      if (srcset) preparedImage.srcset = srcset;
      if (src) preparedImage.src = src;
    });
  }

  /**
   * Preloads the primary image from a detached gallery before it replaces the
   * currently visible gallery. This prevents a temporary alt-text/broken-image
   * state during rapid variant changes.
   * @param {HTMLImageElement} sourceImage
   * @returns {Promise<boolean>}
   */
  #preloadImage(sourceImage) {
    const src = sourceImage.getAttribute('src');
    const srcset = sourceImage.getAttribute('srcset');
    const sizes = sourceImage.getAttribute('sizes');

    if (!src && !srcset) return Promise.resolve(false);

    return new Promise((resolve) => {
      const preloader = new Image();
      let settled = false;

      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(loaded);
      };

      const timeoutId = window.setTimeout(() => finish(false), 5000);
      preloader.decoding = 'async';
      preloader.onload = () => finish(preloader.naturalWidth > 0);
      preloader.onerror = () => finish(false);

      if (sizes) preloader.sizes = sizes;
      if (srcset) preloader.srcset = srcset;
      if (src) preloader.src = src;
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
